import React, { useEffect, useState, useCallback } from 'react';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { Promos } from '../api/admin';

const PromosTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState(filters.q || '');
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => { setSearch(filters.q || ''); }, [filters.q]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Promos.list();
            const list = (result.promos || []).slice().sort((a, b) => {
                if (!a.release_date) return 1;
                if (!b.release_date) return -1;
                return new Date(b.release_date) - new Date(a.release_date);
            });
            setRows(list);
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-select when ?promoId=... is provided.
    useEffect(() => {
        if (!filters.promoId) return;
        const found = rows.find((r) => String(r.promo_id) === String(filters.promoId));
        if (found) setSelected(found);
    }, [filters.promoId, rows]);

    const filtered = search
        ? rows.filter((r) =>
            (r.display_name || '').toLowerCase().includes(search.toLowerCase())
            || String(r.promo_id || '').includes(search))
        : rows;

    const columns = [
        { key: 'id', header: 'ID', width: '70px' },
        { key: 'promo_id', header: 'Promo ID' },
        { key: 'display_name', header: 'Display Name' },
        {
            key: 'release_date',
            header: 'Released',
            render: (r) => (r.release_date ? new Date(r.release_date).toLocaleDateString() : '—'),
        },
        {
            key: 'related',
            header: 'Certs',
            render: (r) => (
                <RelLink tab="certs" filters={{ promoId: r.promo_id }} goTo={goTo}>
                    View certs →
                </RelLink>
            ),
        },
    ];

    const sectionsFor = (r) => [
        {
            title: 'Promo',
            rows: [
                { label: 'ID', value: r.id },
                { label: 'Promo ID', value: r.promo_id },
                { label: 'Display Name', value: r.display_name },
                { label: 'Release', value: r.release_date ? new Date(r.release_date).toLocaleDateString() : '—' },
                { label: 'Release End', value: r.release_date_end ? new Date(r.release_date_end).toLocaleDateString() : '—' },
            ],
        },
        {
            title: 'Related',
            rows: [
                {
                    label: 'Certs in promo',
                    render: () => (
                        <RelLink tab="certs" filters={{ promoId: r.promo_id }} goTo={goTo}>
                            View certs →
                        </RelLink>
                    ),
                },
            ],
        },
    ];

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Filter promos…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn-ghost" onClick={() => goTo({ tab: 'promos' })}>Clear</button>
                <span className="toolbar-spacer" />
                <button className="btn-primary" onClick={() => setCreating(true)}>
                    + New Promo
                </button>
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={filtered}
                    onRowClick={(r) => setSelected(r)}
                    emptyMessage="No promos match."
                />
            )}

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Promo ${selected.promo_id}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? sectionsFor(selected) : []}
            />

            {creating && (
                <CreatePromoModal
                    onClose={() => setCreating(false)}
                    onSaved={() => { setCreating(false); load(); }}
                />
            )}
        </div>
    );
};

const CreatePromoModal = ({ onClose, onSaved }) => {
    const [form, setForm] = useState({ display_name: '', release_date: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await Promos.create(form);
            onSaved();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Create New Promo</h3>
                {error && <p className="error-message">{error}</p>}
                <div className="mapping-section">
                    <div className="mapping-field">
                        <label>Display Name:</label>
                        <input
                            value={form.display_name}
                            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                            placeholder="Enter display name"
                        />
                    </div>
                    <div className="mapping-field">
                        <label>Release Date:</label>
                        <input
                            type="date"
                            value={form.release_date}
                            onChange={(e) => setForm({ ...form, release_date: e.target.value })}
                        />
                    </div>
                    <div className="modal-buttons">
                        <button onClick={onClose} disabled={busy}>Cancel</button>
                        <button
                            className="section-save-button"
                            onClick={submit}
                            disabled={busy || !form.display_name || !form.release_date}
                        >
                            {busy ? 'Saving…' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromosTab;
